import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps, ButtonProps } from "@insite/mobius/Button";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { resolveColor } from "@insite/mobius/utilities";
import breakpointMediaQueries, { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import ResponsiveButton from "@store/Components/Common/ResponsiveButton";
import StoreCallToActionCard, {
    StoreCallToActionCardProps,
    StoreCallToActionCardStyles,
} from "@store/Components/Store/StoreCallToActionCard";
import { PATH } from "@store/constants";
import { useLinkTo } from "@store/Hooks/Common";
import { COMMON, TYPOGRAPHY } from "@store/theme/design";
import { callToActionSpacing, transitionControlStyles } from "@store/theme/mixins";
import { bannerSpacingStyles } from "@store/theme/mixins/banner";
import { ButtonColorPreset, buttonGetColorPreset } from "@store/theme/props/button";
import {
    CarouselArrowDirection,
    dotWrapperStyles,
    getCarouselArrowProps,
    getDotButtonProps,
} from "@store/theme/props/carousel";
import { largeHeadingProps } from "@store/theme/props/typography";
import breakpointValuesMediaQueries from "@store/utilities/breakpointValuesMediaQueries";
import { useEmblaCarousel } from "embla-carousel/react";
import React, { useEffect, useState } from "react";
import { css } from "styled-components";

const imageWidth = 500;
const imageHeight = 715;
const imageRatio = imageHeight / imageWidth;
const imageWidths = ["250px", null, null, "340px", `420px`];
const carouselOffsets = imageWidths.map(x => (x ? `calc(${imageRatio} * -${x})` : null));
const carouselSpacing = ["42px", null, null, "68px", "96px"];
const defaultBackgroundColor = "rgb(37, 57, 74)";
const defaultImagePath = `${PATH.staticFiles}/megabanner-unavailable.png`;

const enum fields {
    backgroundColor = "backgroundColor",
    heading = "heading",
    subheading = "subheading",
    buttonLabel = "buttonLabel",
    buttonLink = "buttonLink",
    buttonVariant = "buttonVariant",
    horizontalAlignment = "horizontalAlignment",
    invertColor = "invertColor",
    slides = "slides",
    image = "image",
    imageAltText = "imageAltText",
    link = "link",
}

interface SlideModel {
    fields: {
        [fields.link]: LinkFieldValue;
        [fields.heading]: string;
        [fields.subheading]: string;
        [fields.image]: string;
        [fields.imageAltText]: string;
    };
}

interface Props extends WidgetProps {
    fields: {
        [fields.backgroundColor]: string;
        [fields.heading]: string;
        [fields.subheading]: string;
        [fields.buttonLabel]: string;
        [fields.buttonLink]: LinkFieldValue;
        [fields.buttonVariant]: "primary" | "secondary" | "tertiary";
        [fields.horizontalAlignment]: string;
        [fields.invertColor]: boolean;
        [fields.slides]: SlideModel[];
    };
}

export interface MegaBannerContentStyles {
    container?: InjectableCss;
    scaler?: InjectableCss;
    wrapper?: InjectableCss;
    heading?: TypographyPresentationProps;
    subheading?: TypographyPresentationProps;
    buttonWrapper?: InjectableCss;
}

export interface MegaBannerCarouselStyles {
    container?: InjectableCss;
    wrapper?: InjectableCss;
    slider?: InjectableCss;
    slides?: InjectableCss;
    slide?: InjectableCss;
    heading?: TypographyPresentationProps;
    subheading?: TypographyPresentationProps;
    callToActionCard?: StoreCallToActionCardStyles;
    dotsWrapper?: InjectableCss;
    dotButton?: ButtonPresentationProps;
    dotButtonSelected?: ButtonPresentationProps;
    prevArrowWrapper?: InjectableCss;
    prevArrowButton?: ButtonPresentationProps;
    nextArrowWrapper?: InjectableCss;
    nextArrowButton?: ButtonPresentationProps;
}

export interface MegaBannerStyles {
    wrapper?: InjectableCss;
    content?: MegaBannerContentStyles;
    carousel?: MegaBannerCarouselStyles;
}

const getArrowWrapperProps = (direction: "right" | "left") => ({
    css: css`
        position: absolute;
        ${direction}: 0;
        z-index: 1;
        display: none;
        width: 0;
        height: 0;

        ${props =>
            breakpointValuesMediaQueries(
                props.theme,
                [
                    { values: [null, "block"], properties: ["display"] },
                    {
                        values: imageWidths.map(x => (x ? `calc(${imageRatio} * ${x} / 2)` : null)),
                        properties: ["top"],
                    },
                ],
                mediaOptions.min,
            )}
    `,
});

const getArrowProps = (direction: CarouselArrowDirection) => {
    const props = getCarouselArrowProps(direction);

    return {
        ...props,
        css: css`
            ${props?.css}
            transform: translate3d(-50%, -50%, 0);
            transition: ${transitionControlStyles()};

            &:disabled {
                opacity: 0;
                pointer-events: none;
            }
        `,
    };
};

export const megaBannerStyles: MegaBannerStyles = {
    wrapper: {
        css: css`
            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [{ values: COMMON.spacing.spacedContent, properties: ["margin-top", "margin-bottom"] }],
                    mediaOptions.min,
                )}
        `,
    },
    content: {
        container: {
            css: css`
                ${bannerSpacingStyles("padding", "horizontal")}
                align-items: center;
            `,
        },
        wrapper: {
            css: css`
                padding: 40px 0;

                ${props =>
                    breakpointValuesMediaQueries(
                        props.theme,
                        [
                            { values: [null, "50%", "40%", "33%", null], properties: ["width"] },
                            { values: [null, "3%", null, "4%", "7.5%"], properties: ["padding-right", "padding-left"] },
                        ],
                        mediaOptions.min,
                    )}
            `,
        },
        heading: largeHeadingProps,
        subheading: { variant: "p" },
        buttonWrapper: {
            css: css`
                ${callToActionSpacing()}
            `,
        },
    },
    carousel: {
        container: {
            css: css`
                ${bannerSpacingStyles("padding", "horizontal")}
                ${props =>
                    breakpointMediaQueries(
                        props.theme,
                        [
                            css`
                                padding-right: 0;
                                padding-left: 0;
                            `,
                        ],
                        mediaOptions.max,
                    )}
            `,
        },
        wrapper: {
            css: css`
                position: relative;
                ${props =>
                    breakpointValuesMediaQueries(
                        props.theme,
                        [{ values: [null, "50%", "60%", "68%", "69.5%"], properties: ["width"] }],
                        mediaOptions.min,
                    )}
            `,
        },
        slider: {
            css: css`
                ${props =>
                    breakpointValuesMediaQueries(
                        props.theme,
                        [{ values: [COMMON.spacing.side[0]], properties: ["padding-right", "padding-left"] }],
                        mediaOptions.max,
                    )}
            `,
        },
        slides: {
            css: css`
                ${props =>
                    breakpointValuesMediaQueries(
                        props.theme,
                        [{ values: ["0 -3px", null, null, "0 -5px"], properties: ["margin"] }],
                        mediaOptions.min,
                    )}
            `,
        },
        slide: {
            css: css`
                box-sizing: content-box;
                ${props =>
                    breakpointValuesMediaQueries(
                        props.theme,
                        [{ values: ["0 3px", null, null, "0 5px"], properties: ["padding"] }],
                        mediaOptions.min,
                    )}
            `,
        },
        callToActionCard: {
            link: {
                css: css`
                    box-sizing: border-box;
                `,
            },
            title: {
                forwardAs: "h3",
                css: css`
                    ${({ theme }) =>
                        breakpointValuesMediaQueries(
                            theme,
                            [{ values: ["20px", null, null, "23px", "25px"], properties: ["font-size"] }],
                            mediaOptions.min,
                        )}
                `,
            },
            description: {
                css: css`
                    ${props =>
                        breakpointValuesMediaQueries(
                            props.theme,
                            [
                                {
                                    values: [
                                        TYPOGRAPHY.size.smaller,
                                        null,
                                        null,
                                        TYPOGRAPHY.size.small,
                                        TYPOGRAPHY.size.base,
                                    ],
                                    properties: ["font-size"],
                                },
                            ],
                            mediaOptions.min,
                        )}
                `,
            },
        },
        prevArrowWrapper: getArrowWrapperProps("left"),
        prevArrowButton: getArrowProps("left"),
        nextArrowWrapper: getArrowWrapperProps("right"),
        nextArrowButton: getArrowProps("right"),
        dotsWrapper: {
            css: css`
                ${dotWrapperStyles()}
                margin-top: 10px;
                color: ${getColor("text")};

                ${props =>
                    breakpointValuesMediaQueries(
                        props.theme,
                        [{ values: [null, "none"], properties: ["display"] }],
                        mediaOptions.min,
                    )}
            `,
        },
        dotButton: getDotButtonProps(),
        dotButtonSelected: getDotButtonProps(true),
    },
};
const styles = megaBannerStyles;

const MegaBanner: React.FC<Props> = ({
    fields: {
        heading,
        subheading,
        buttonLink: buttonLinkField,
        buttonLabel,
        buttonVariant,
        invertColor,
        horizontalAlignment,
        slides,
        backgroundColor,
    },
}) => {
    const buttonLink = useGetLink(buttonLinkField);
    const onLinkClick = useLinkTo(buttonLink?.url);
    const slideLinks = useGetLinks(slides, x => x.fields.link);
    const [emblaRef, embla] = useEmblaCarousel();
    const [snapIndex, setSnapIndex] = useState(0);
    const [snapList, setSnapList] = useState<number[]>();

    useEffect(() => {
        embla
            ?.on("select", () => {
                setSnapIndex(embla.selectedScrollSnap());
            })
            .on("reInit", () => {
                setSnapList(embla.scrollSnapList());
            });
    }, [embla]);

    useEffect(() => {
        if (!embla || !fields.slides?.length) {
            return;
        }

        embla?.reInit({
            align: "start",
            containScroll: "trimSnaps",
        });
    }, [embla, fields.slides]);

    const hasPaging = snapList && snapList.length > 1;
    const alignRight = horizontalAlignment === "right";
    const buttonProps: ButtonProps = {
        variant: buttonVariant,
        onClick: onLinkClick,
        ...(invertColor ? buttonGetColorPreset(ButtonColorPreset.Invert) : null),
    };

    const contentContainerStyles = css`
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        color: ${props => resolveColor(invertColor ? "text.contrast" : "text", props.theme)};
        ${backgroundColor ? `background-color: ${backgroundColor};` : null}

        ${props =>
            breakpointMediaQueries(
                props.theme,
                [
                    null,
                    css`
                        flex-direction: ${alignRight ? "row-reverse" : "row"};
                    `,
                ],
                mediaOptions.min,
            )}

        ${styles.content?.container?.css}
    `;

    const contentWrapperStyles = css`
        flex-shrink: 0;
        width: 100%;
        ${styles.content?.wrapper?.css}
    `;

    const contentScalerStyles = css`
        flex-shrink: 0;

        &::before {
            content: "";
            display: block;
            padding-top: ${imageRatio * 100}%;
        }

        ${props =>
            breakpointValuesMediaQueries(
                props.theme,
                [
                    { values: imageWidths, properties: ["width"] },
                    {
                        values: [...carouselSpacing.slice(0, 1).map(x => (x ? `-${x}` : null)), 0],
                        properties: ["margin-bottom"],
                    },
                ],
                mediaOptions.min,
            )}

        ${styles.content?.scaler?.css}
    `;

    const carouselContainerStyles = css`
        display: flex;
        ${!alignRight ? "justify-content: flex-end;" : null}

        ${props =>
            breakpointValuesMediaQueries(
                props.theme,
                [
                    { values: carouselSpacing, properties: ["padding-top"] },
                    { values: carouselOffsets, properties: ["margin-top"] },
                ],
                mediaOptions.min,
            )}

        ${styles.carousel?.container?.css}
    `;

    const carouselWrapperStyles = css`
        flex-shrink: 0;
        width: 100%;
        ${styles.carousel?.wrapper?.css}
    `;

    const carouselSliderStyles = css`
        overflow: hidden;
        ${styles.carousel?.slider?.css}
    `;

    const carouselSlidesStyles = css`
        display: flex;
        ${styles.carousel?.slides?.css}
    `;

    const carouselSlideStyles = css`
        position: relative;
        flex-shrink: 0;

        ${props =>
            breakpointValuesMediaQueries(
                props.theme,
                [{ values: imageWidths, properties: ["width"] }],
                mediaOptions.min,
            )}

        ${styles.carousel?.slide?.css}
    `;

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.content?.container} css={contentContainerStyles}>
                <StyledWrapper {...styles.content?.wrapper} css={contentWrapperStyles}>
                    {heading ? <Typography {...styles.content?.heading}>{heading}</Typography> : null}
                    {subheading ? <Typography {...styles.content?.subheading}>{subheading}</Typography> : null}
                    {buttonLink?.url ? (
                        <ResponsiveButton buttonProps={buttonProps} {...styles.content?.buttonWrapper}>
                            {buttonLabel || buttonLink.title || buttonLink.url}
                        </ResponsiveButton>
                    ) : null}
                </StyledWrapper>
                <StyledWrapper {...styles.content?.scaler} css={contentScalerStyles}></StyledWrapper>
            </StyledWrapper>
            {slides.length ? (
                <StyledWrapper {...styles.carousel?.container} css={carouselContainerStyles}>
                    <StyledWrapper {...styles.carousel?.wrapper} css={carouselWrapperStyles}>
                        {hasPaging ? (
                            <StyledWrapper {...styles.carousel?.prevArrowWrapper}>
                                <Button
                                    {...styles.carousel?.prevArrowButton}
                                    onClick={() => embla?.scrollPrev()}
                                    disabled={snapIndex === 0}
                                >
                                    <VisuallyHidden>{translate("Previous")}</VisuallyHidden>
                                </Button>
                            </StyledWrapper>
                        ) : null}
                        <StyledWrapper {...styles.carousel?.slider} css={carouselSliderStyles} ref={emblaRef}>
                            <StyledWrapper {...styles.carousel?.slides} css={carouselSlidesStyles}>
                                {slides.map(({ fields: slide }, index) => {
                                    const slideLink = slideLinks[index];
                                    const title = slide.heading || slideLink?.title;
                                    const callToActionCardProps: StoreCallToActionCardProps = {
                                        link: { href: slideLink?.url },
                                        title,
                                        content: slide.subheading,
                                        image: {
                                            src: slide.image || defaultImagePath,
                                            altText: slide.image
                                                ? slide.imageAltText || title
                                                : translate("Image Unavailable"),
                                        },
                                        imageRatio,
                                        extendedStyles: styles.carousel?.callToActionCard,
                                    };

                                    return (
                                        <StyledWrapper
                                            key={index} // eslint-disable-line react/no-array-index-key
                                            {...styles.carousel?.slide}
                                            css={carouselSlideStyles}
                                        >
                                            <StoreCallToActionCard {...callToActionCardProps} />
                                        </StyledWrapper>
                                    );
                                })}
                            </StyledWrapper>
                        </StyledWrapper>
                        {hasPaging ? (
                            <StyledWrapper {...styles.carousel?.nextArrowWrapper}>
                                <Button
                                    {...styles.carousel?.nextArrowButton}
                                    onClick={() => embla?.scrollNext()}
                                    disabled={snapIndex + 1 === snapList?.length}
                                >
                                    <VisuallyHidden>{translate("Next")}</VisuallyHidden>
                                </Button>
                            </StyledWrapper>
                        ) : null}
                        {hasPaging ? (
                            <StyledWrapper {...styles.carousel?.dotsWrapper}>
                                {snapList?.map((_, i) => (
                                    <Button
                                        key={i} // eslint-disable-line react/no-array-index-key
                                        {...(i === snapIndex
                                            ? styles.carousel?.dotButtonSelected
                                            : styles.carousel?.dotButton)}
                                        onClick={() => embla && embla.scrollTo(i)}
                                    />
                                ))}
                            </StyledWrapper>
                        ) : null}
                    </StyledWrapper>
                </StyledWrapper>
            ) : null}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: MegaBanner,
    definition: {
        group: "Basic",
        icon: "image-landscape",
        displayName: "Mega Banner",
        fieldDefinitions: [
            {
                name: fields.backgroundColor,
                displayName: "Background Color",
                editorTemplate: "ColorPickerField",
                fieldType: "General",
                defaultValue: defaultBackgroundColor,
                tooltip: `Default: ${defaultBackgroundColor}, Secondary: rgb(36, 76, 90), Tertiary: rgb(0, 114, 120)`,
            },
            {
                name: fields.invertColor,
                displayName: "White Text Color",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: true,
            },
            {
                name: fields.heading,
                displayName: "Title",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
            },
            {
                name: fields.subheading,
                displayName: "Content",
                editorTemplate: "MultilineTextField",
                fieldType: "Translatable",
                defaultValue: "",
            },
            {
                name: fields.buttonLink,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                fieldType: "General",
                defaultValue: { type: "Page", value: "" },
            },
            {
                name: fields.buttonLabel,
                displayName: "Button Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                isVisible: widget => widget.fields.buttonLink?.value,
            },
            {
                name: fields.buttonVariant,
                displayName: "Button Variant",
                editorTemplate: "DropDownField",
                fieldType: "General",
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                    { displayName: "Tertiary", value: "tertiary" },
                ],
                hideEmptyOption: true,
                defaultValue: "primary",
                isVisible: widget => widget.fields.buttonLink?.value,
            },
            {
                name: fields.horizontalAlignment,
                displayName: "Alignment",
                editorTemplate: "DropDownField",
                fieldType: "General",
                options: [
                    { displayName: "Left", value: "left" },
                    { displayName: "Right", value: "right" },
                ],
                hideEmptyOption: true,
                defaultValue: "left",
            },
            {
                name: fields.slides,
                displayName: "Slides",
                editorTemplate: "ListField",
                fieldType: "Translatable",
                defaultValue: [],
                getDisplay: (item: HasFields) => item.fields.heading,
                fieldDefinitions: [
                    {
                        name: fields.link,
                        displayName: "Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                    },
                    {
                        name: fields.heading,
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: fields.subheading,
                        displayName: "Content",
                        editorTemplate: "MultilineTextField",
                        defaultValue: "",
                    },
                    {
                        name: fields.image,
                        displayName: "Image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        tooltip: `${imageWidth} x ${imageHeight}`,
                    },
                    {
                        name: fields.imageAltText,
                        displayName: "Image Alt Text",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
